import * as TagAPI from "api/TagAPI";
import * as ModelGroupAPI from "api/ModelGroupAPI";

import * as runReducer from "containers/Run/reducer";
import * as globalReducer from "containers/App/reducer";
import * as deploymentsReducer from "containers/Deployments/reducer";

import { isEnabled } from "utils/featureFlags";

const UPDATE_FIELD = "modelGroup/UPDATE_FIELD";
const UPDATE_GROUP = "modelGroup/UPDATE_GROUP";
const UPDATE_GROUPS = "modelGroup/UPDATE_GROUPS";
const RESET_GROUPS = "modelGroup/RESET_GROUPS";

const initialState = {};

function modelGroupReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    case UPDATE_GROUP:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ...action.data
        }
      };
    case UPDATE_GROUPS:
      return {
        ...action.data
      };
    case RESET_GROUPS:
      return { ...initialState };
    default:
      return state;
  }
}

/* Selectors */
export function getIsModelSplits() {
  return isEnabled("model-splits");
}

export function getBase(state) {
  return state.modelGroup;
}

export function getModelGroupArr(state) {
  const modelGroups = getBase(state);

  return Object.keys(modelGroups)
    .map((key) => modelGroups[key])
    .sort((a, b) => b.id - a.id)
    .reverse();
}

export function getModelGroup(state, id, transform = true) {
  const modelGroup = getBase(state)[id];

  if (transform === false) {
    return modelGroup;
  } else {
    const run = runReducer.getTransformedJob(state, id);
    const best = run.transformedModels && run.transformedModels[0] ? run.transformedModels[0] : undefined;
    const isContinuous = best && best.metrics && best.metrics.sum_over_predict;
    const bestImpact =
      best &&
      (best.maxIteration.displayImpact
        ? Math.round(best.maxIteration.displayImpact)
        : isContinuous
          ? Math.round(best.metrics.overImpact + best.metrics.underImpact)
          : Math.round(best.checkMax));
    const averageImpact = run.setup && !isNaN(run.setup.processLimit) ? bestImpact / run.setup.processLimit : undefined;

    return modelGroup && modelGroup.models
      ? {
          ...modelGroup,
          models: [
            ...modelGroup.models.map((m) => ({
              ...m,
              job_id: m.job_id === 0 ? undefined : m.job_id,
              models: m.job_id === 0 ? undefined : runReducer.getModelsArr(state, m.job_id),
              isDone: m.job_id === 0 ? false : runReducer.getIsDone(state, m.job_id),
              name:
                m.conditions === undefined ? "Overall" : m.conditions.map((c) => c.column + " - " + c.value).join(",")
            })),
            {
              ...modelGroup.head,
              models: runReducer.getModelsArr(state, id),
              impact: bestImpact,
              averageImpact: isNaN(averageImpact) ? undefined : averageImpact,
              isDone: runReducer.getIsDone(state, id),
              name: "Overall"
            }
          ]
        }
      : undefined;
  }
}

export function getIsLoading(state, id) {
  return getModelGroup(state, id, false) ? getModelGroup(state, id, false).isLoading : undefined;
}

export function getLinkedModels(state, id) {
  return getBase(state)[id] ? getBase(state)[id].value : undefined;
}

export function getLinkedModelsArr(state, id) {
  return getBase(state)[id]
    ? Object.keys(getBase(state)[id].value).map((v) => ({
        ...getBase(state)[id].value[v],
        name: v
      }))
    : undefined;
}

export function getValues(state, id) {
  return getBase(state)[id] ? getBase(state)[id].value : undefined;
}

export function getSelectedJob(state, id) {
  let selectedId = id;
  const modelGroup = (getModelGroup(state, id) || {}).models;
  const specialIndex = runReducer.getSpecialIndex(state, id);

  if (specialIndex !== undefined && modelGroup) {
    const selectedModel = modelGroup[specialIndex];
    if (selectedModel) {
      selectedId = selectedModel.job_id;
    }
  }
  return parseInt(selectedId);
}

export function getHeadModel(state, id) {
  const modelGroup = getModelGroup(state, id);
  if (!modelGroup || !modelGroup.head) {
    return null;
  }
  const model = runReducer.getModel(state, id, modelGroup.head.model_name);
  return model;
}

/* Actions */
export function updateHover(id, hover) {
  return {
    type: UPDATE_FIELD,
    id: id,
    key: "hover",
    data: hover
  };
}

export function updateIsLoading(id, isLoading = false) {
  return {
    type: UPDATE_FIELD,
    id: id,
    key: "isLoading",
    data: isLoading
  };
}

export function updateModelGroup(id, modelGroup, updateServer = false) {
  if (updateServer === true) {
    ModelGroupAPI.patchModelGroup(modelGroup.id, {
      models: [...modelGroup.models]
    });
  }

  return {
    type: UPDATE_GROUP,
    id: id,
    data: modelGroup
  };
}

export function updateModelGroups(modelGroup) {
  return {
    type: UPDATE_GROUPS,
    data: modelGroup
  };
}

export function deleteGroup(originalId) {
  return (dispatch, getState) => {
    const modelGroups = { ...getBase(getState()) };

    let retModelGroups = {};

    modelGroups &&
      Object.keys(modelGroups)
        .filter((id) => id !== originalId)
        .forEach((id) => (retModelGroups[id] = { ...modelGroups[id] }));

    return dispatch(updateModelGroups(retModelGroups));
  };
}

export function resetModelGroups() {
  return { type: RESET_GROUPS };
}

export function transformModelGroup(modelGroup) {
  return {
    ...modelGroup,
    isLoading: false
  };
}

export function fetchAll() {
  return (dispatch, getState) => {
    dispatch(globalReducer.updateIsModelGroupsLoading(true));
    return ModelGroupAPI.getModelGroups().then((response) => {
      let modelGroups = {};

      response.forEach((modelGroup) => {
        if (modelGroup.head && modelGroup.head.job_id !== undefined) {
          modelGroups[modelGroup.head.job_id] = transformModelGroup(modelGroup);
        }
      });

      dispatch(globalReducer.updateIsModelGroupsLoading(false));

      return dispatch(updateModelGroups(modelGroups));
    });
  };
}

export function createModelGroup(jobId) {
  return (dispatch, getState) => {
    dispatch(updateIsLoading(jobId, true));
    const selModel = runReducer.getFeatureSelection(getState(), jobId);

    let retModel = "aible-relative-impact";
    // TODO: Check if this is even still necessary, selModel should always be a string
    if (selModel) {
      switch (typeof selModel) {
        case "string":
          retModel = selModel;
          break;
        case "object":
          retModel = selModel.id;
          break;
      }
    }

    return ModelGroupAPI.createModelGroup(jobId, retModel).then((response) => {
      dispatch(updateIsLoading(jobId, false));
      return dispatch(updateModelGroup(jobId, transformModelGroup(response)));
    });
  };
}

export function deleteModelGroup(originalId, modelGroupId) {
  return (dispatch, getState) => {
    dispatch(updateIsLoading(originalId, true));
    return ModelGroupAPI.deleteModelGroup(modelGroupId).then((response) => {
      dispatch(updateIsLoading(originalId, false));
      return dispatch(deleteGroup(originalId));
    });
  };
}

export function deleteSubModelGroup(originalId, subModelGroupId) {
  return (dispatch, getState) => {
    const modelGroup = getModelGroup(getState(), originalId, false);

    const original = modelGroup.models;

    const retModelGroups = {
      models: [...modelGroup.models.filter((m, i) => i !== subModelGroupId)]
    };
    dispatch(updateIsLoading(originalId, true));
    dispatch(updateModelGroup(originalId, { ...modelGroup, ...retModelGroups }));

    return ModelGroupAPI.patchModelGroup(modelGroup.id, retModelGroups)
      .then((response) => {
        dispatch(updateIsLoading(originalId, false));
        return dispatch(updateModelGroup(originalId, transformModelGroup(response)));
      })
      .catch((err) => {
        dispatch(updateModelGroup(originalId, { ...modelGroup, models: original }));
      });
  };
}

function createNameFromSplit(specialVals = []) {
  const splitInfo = {};
  let name = "";
  specialVals.forEach((elem) => {
    if (splitInfo[elem.field]) {
      splitInfo[elem.field].push(elem.fieldValue);
    } else {
      splitInfo[elem.field] = [elem.fieldValue];
    }
  });
  Object.keys(splitInfo).forEach((field) => {
    if (name) {
      name += "; ";
    }
    name += field + " = " + splitInfo[field].join(",");
  });
  return name;
}

export function addRunToSubGroup(originalId, subJobId, subGroupIndex) {
  return (dispatch, getState) => {
    const modelGroup = getModelGroup(getState(), originalId, false);

    let retModels = { models: [...modelGroup.models] };
    if (retModels.models[subGroupIndex] !== undefined) {
      retModels.models[subGroupIndex] = {
        ...retModels.models[subGroupIndex],
        job_id: subJobId
      };
    }

    ModelGroupAPI.patchModelGroup(modelGroup.id, retModels).then((response) =>
      dispatch(updateModelGroup(originalId, transformModelGroup(response)))
    );

    dispatch(runReducer.updateRelatedId(originalId, subJobId));

    return dispatch(runReducer.fetchRun(subJobId)).then(() => dispatch(runReducer.handleEndStep(subJobId)));
  };
}

export function linkModelToGroup(originalId, specialVals) {
  return (dispatch, getState) => {
    const modelGroup = getModelGroup(getState(), originalId, false);

    const retModelGroups = {
      models: [
        {
          job_id: null,
          model_name: null,
          conditions: specialVals
            ? specialVals.map((s) => ({
                column: s.field,
                value: s.fieldValue,
                operand: "="
              }))
            : []
        },
        ...modelGroup.models
      ]
    };

    return ModelGroupAPI.patchModelGroup(modelGroup.id, retModelGroups).then((response) =>
      dispatch(updateModelGroup(originalId, transformModelGroup(response)))
    );
  };
}

export function updateDeployedModel(originalId, subGroupIndex, model) {
  return (dispatch, getState) => {
    const modelGroup = getModelGroup(getState(), originalId, false);

    let retModelGroups = {
      models: [...modelGroup.models]
    };

    retModelGroups.models[subGroupIndex].model_name = model;

    const deployment = deploymentsReducer.getDeployment(getState(), originalId, model);
    if (deployment === undefined) {
      const groupJobId = retModelGroups.models[subGroupIndex].job_id;
      groupJobId !== 0 && dispatch(deploymentsReducer.handleRestfulDeploy(groupJobId, model));
    }

    return ModelGroupAPI.patchModelGroup(modelGroup.id, retModelGroups).then((response) =>
      dispatch(updateModelGroup(originalId, transformModelGroup(response)))
    );
  };
}

export function updatePriority(originalId, specialId, priorityDelta) {
  return (dispatch, getState) => {
    dispatch(updateIsLoading(originalId, true));
    const modelGroup = getModelGroup(getState(), originalId, false);
    let retModelGroups = { models: [...modelGroup.models] };

    const original = modelGroup.models;

    if (priorityDelta === 1) {
      [retModelGroups.models[specialId - 1], retModelGroups.models[specialId]] = [
        retModelGroups.models[specialId],
        retModelGroups.models[specialId - 1]
      ];
    } else if (priorityDelta === -1) {
      [retModelGroups.models[specialId], retModelGroups.models[specialId + 1]] = [
        retModelGroups.models[specialId + 1],
        retModelGroups.models[specialId]
      ];
    }

    dispatch(updateModelGroup(originalId, { ...modelGroup, ...retModelGroups }));

    return ModelGroupAPI.patchModelGroup(modelGroup.id, retModelGroups)
      .then((response) => {
        dispatch(updateIsLoading(originalId, false));
        return dispatch(updateModelGroup(originalId, transformModelGroup(response)));
      })
      .catch((err) => {
        dispatch(updateModelGroup(originalId, { ...modelGroup, models: original }));
      });
  };
}

export default modelGroupReducer;
